import {
  coachGetExerciseHistoryQuery,
  coachGetExerciseDetailQuery,
  coachGetFavoriteExercisesQuery,
  coachEditExerciseHistoryMutation,
  coachDeleteExerciseHistoryMutation,
  coachToggleFavoriteExerciseMutation,
} from '@witness/graphql';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useState, useCallback } from 'react';

const useTraineeExerciseHistory = (traineeUid) => {
  const [selectedExercise, setSelectedExercise] = useState(null);

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useQuery(coachGetExerciseHistoryQuery, {
    variables: { record: { traineeUid } },
    skip: !traineeUid,
    fetchPolicy: 'network-only',
  });

  const {
    data: detailData,
    loading: detailLoading,
    refetch: refetchDetail,
  } = useQuery(coachGetExerciseDetailQuery, {
    variables: {
      record: {
        traineeUid,
        exerciseUid: selectedExercise?.exerciseUid,
      },
    },
    skip: !traineeUid || !selectedExercise?.exerciseUid,
    fetchPolicy: 'network-only',
  });

  const {
    data: favoritesData,
    loading: favoritesLoading,
    refetch: refetchFavorites,
  } = useQuery(coachGetFavoriteExercisesQuery, {
    variables: { record: { traineeUid } },
    skip: !traineeUid,
  });

  const [editHistory] = useMutation(coachEditExerciseHistoryMutation);
  const [deleteHistory] = useMutation(coachDeleteExerciseHistoryMutation);
  const [toggleFavorite] = useMutation(coachToggleFavoriteExerciseMutation);

  const handleEditHistory = useCallback(
    async (entryUid, fields) => {
      await editHistory({
        variables: {
          record: { traineeUid, uid: entryUid, ...fields },
        },
      });
      refetchHistory();
      if (selectedExercise) refetchDetail();
    },
    [editHistory, traineeUid, refetchHistory, refetchDetail, selectedExercise],
  );

  const handleDeleteHistory = useCallback(
    async (entryUid) => {
      await deleteHistory({
        variables: {
          record: { traineeUid, uid: entryUid },
        },
      });
      refetchHistory();
      if (selectedExercise) refetchDetail();
    },
    [deleteHistory, traineeUid, refetchHistory, refetchDetail, selectedExercise],
  );

  const handleToggleFavorite = useCallback(
    async (exerciseUid) => {
      await toggleFavorite({
        variables: {
          record: { traineeUid, exerciseUid },
        },
      });
      refetchFavorites();
    },
    [toggleFavorite, traineeUid, refetchFavorites],
  );

  return {
    exerciseHistory: historyData?.coachGetExerciseHistory || [],
    historyLoading,
    refetchHistory,

    exerciseDetail: detailData?.coachGetExerciseDetail || [],
    detailLoading,
    selectedExercise,
    setSelectedExercise,

    favoriteExercises: favoritesData?.coachGetFavoriteExercises || [],
    favoritesLoading,

    handleEditHistory,
    handleDeleteHistory,
    handleToggleFavorite,
  };
};

export default useTraineeExerciseHistory;
